import type { EvidenceBalance } from "../types";
import { formatDivergence, formatPointRange, formatProbabilityLabel } from "../lib/format";

interface ProbabilityDivergenceBarProps {
  marketProbability?: number | null;
  ftmEstimateLow?: number | null;
  ftmEstimateHigh?: number | null;
  divergenceLow?: number | null;
  divergenceHigh?: number | null;
  evidenceBalance?: EvidenceBalance | null;
  compact?: boolean;
  locked?: boolean;
}

function validProbability(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && Number.isFinite(value) && value >= 0 && value <= 1;
}

export function ProbabilityDivergenceBar({
  marketProbability,
  ftmEstimateLow,
  ftmEstimateHigh,
  divergenceLow,
  divergenceHigh,
  evidenceBalance,
  compact = false,
  locked = false,
}: ProbabilityDivergenceBarProps) {
  const hasMarket = validProbability(marketProbability);
  const hasRange = validProbability(ftmEstimateLow) && validProbability(ftmEstimateHigh) && ftmEstimateLow <= ftmEstimateHigh;

  if (!hasRange) {
    return (
      <div className={`divergence-bar divergence-bar-unavailable ${compact ? "divergence-bar-compact" : ""}`}>
        {hasMarket ? <div className="divergence-bar-market-only"><span className="divergence-bar-label">Market {formatProbabilityLabel(marketProbability)}</span><span className="divergence-bar-market-point" style={{ left: `${(marketProbability ?? 0) * 100}%` }} /></div> : null}
        <p className="divergence-bar-unavailable-copy">{locked ? "FTM Estimate locked" : "FTM estimate unavailable"}</p>
      </div>
    );
  }

  const low = ftmEstimateLow as number;
  const high = ftmEstimateHigh as number;
  const market = hasMarket ? marketProbability as number : null;
  const calculatedLow = market === null ? null : low - market;
  const calculatedHigh = market === null ? null : high - market;
  const gapLow = divergenceLow ?? calculatedLow;
  const gapHigh = divergenceHigh ?? calculatedHigh;
  const isPass = evidenceBalance === "PASS";
  const labelsNeedSpace = market !== null && Math.abs(((low + high) / 2) - market) < 0.2;
  const ariaLabel = [
    market === null ? "Market unavailable" : `Market ${formatProbabilityLabel(market)}`,
    `FTM ${formatPointRange(low, high)}`,
    isPass ? "PASS, no meaningful divergence" : gapLow !== null && gapHigh !== null ? formatDivergence(gapLow, gapHigh) : "Divergence unavailable",
  ].join(". ");

  return (
    <div className={`divergence-bar ${compact ? "divergence-bar-compact" : ""}`}>
      <div className="divergence-bar-header">
        <span className="divergence-bar-scale-label">0%</span>
        <span className="divergence-bar-scale-label">50%</span>
        <span className="divergence-bar-scale-label">100%</span>
      </div>
      <div className={`divergence-bar-visual ${labelsNeedSpace ? "divergence-bar-labels-close" : ""}`} role="img" aria-label={ariaLabel}>
        <span className="divergence-bar-track" />
        {market !== null ? <span className="divergence-bar-market-point" style={{ left: `${market * 100}%` }} /> : null}
        <span className="divergence-bar-range" style={{ left: `${low * 100}%`, width: `${Math.max((high - low) * 100, 1)}%` }} />
        {market !== null && !isPass && gapLow !== null && gapHigh !== null ? (
          <span className="divergence-bar-gap" style={{ left: `${Math.min(market, low) * 100}%`, width: `${Math.abs(low - market) * 100}%` }} />
        ) : null}
        {market !== null ? <span className="divergence-bar-market-label" style={{ left: `${market * 100}%` }}>Market {formatProbabilityLabel(market)}</span> : null}
        <span className="divergence-bar-range-label" style={{ left: `${((low + high) / 2) * 100}%` }}>FTM {formatPointRange(low, high)}</span>
      </div>
      <div className="divergence-bar-footer">
        {isPass ? <span className="divergence-bar-pass">PASS · No meaningful divergence</span> : gapLow !== null && gapHigh !== null ? <span className="divergence-bar-gap-label">{formatDivergence(gapLow, gapHigh)}</span> : <span>Divergence unavailable</span>}
        <span className="divergence-bar-uncertainty">Range width communicates uncertainty</span>
      </div>
    </div>
  );
}
